<template>
  <article class="dashboard-card preika-insight-card preika-approval-aging">
    <div class="preika-insight-heading">
      <div>
        <p class="preika-insight-heading__eyebrow">APPROVAL FLOW</p>
        <h2>承認滞留分析</h2>
        <p class="preika-insight-heading__description">
          承認待ちの件数と経過日数を可視化します。
        </p>
      </div>
      <span class="preika-insight-badge preika-insight-badge--neutral">
        {{ scopeLabel }}
      </span>
    </div>

    <div v-if="loading" class="preika-insight-loading">
      <span class="preika-insight-loading__spinner"></span>
      承認待ちを分析しています
    </div>

    <div v-else-if="error" class="preika-insight-error" role="alert">
      <div>
        <strong>承認滞留を取得できませんでした</strong>
        <small>承認待ち一覧は従来どおり利用できます。</small>
      </div>
      <button type="button" @click="$emit('retry')">再取得</button>
    </div>

    <template v-else-if="totalCount">
      <div class="preika-aging-kpis">
        <div><small>承認待ち</small><strong>{{ totalCount }}</strong><span>件</span></div>
        <div><small>平均滞留</small><strong>{{ value.averageDays || 0 }}</strong><span>日</span></div>
        <div :class="{ 'preika-aging-kpi--danger': Number(value.oldestDays) >= 8 }">
          <small>最長滞留</small><strong>{{ value.oldestDays || 0 }}</strong><span>日</span>
        </div>
      </div>

      <div class="preika-aging-distribution">
        <div class="preika-aging-distribution__bar" aria-label="承認待ちの滞留日数分布">
          <span
            v-for="segment in segments"
            :key="segment.key"
            :class="'preika-aging-segment--' + segment.tone"
            :style="{ width: segment.width + '%' }"
            :title="segment.label + ': ' + segment.count + '件'"
          ></span>
        </div>
        <div class="preika-aging-legend">
          <span v-for="segment in segments" :key="segment.key">
            <i :class="'preika-aging-legend__dot--' + segment.tone"></i>
            {{ segment.label }} <strong>{{ segment.count }}</strong>
          </span>
        </div>
      </div>

      <div class="preika-aging-list-heading">
        <div><small>OLDEST FIRST</small><strong>古い承認待ち</strong></div>
        <router-link to="/submission_approval/submission_management">一覧を見る<CIcon name="cil-arrow-right"/></router-link>
      </div>

      <div class="preika-aging-list">
        <router-link
          v-for="item in items"
          :key="item.id"
          :to="item.route || '/submission_approval/submission_management'"
          class="preika-aging-item"
        >
          <span class="preika-aging-item__days" :class="agingTone(item.waitingDays)">
            <strong>{{ item.waitingDays }}</strong><small>日</small>
          </span>
          <span class="preika-aging-item__content">
            <strong>{{ item.documentName }}</strong>
            <span>{{ item.userName }}<small v-if="item.approverName">承認者 {{ item.approverName }}</small></span>
          </span>
          <span class="preika-aging-item__date">{{ item.submissionAt || '日時未設定' }}</span>
          <CIcon name="cil-chevron-right" class="preika-aging-item__arrow"/>
        </router-link>
      </div>

      <!--
        会社全体だと承認待ちが数百件になりうる。全部を描いても読めないので
        古い順に先頭だけ出す。**黙って切らない。** 残りの件数を必ず出し、
        全部見るときは上の「一覧を見る」から承認一覧へ行く。
      -->
      <p v-if="hiddenItems > 0" class="preika-aging-more">
        ほか {{ hiddenItems }}件。すべて見るには一覧へ。
      </p>
    </template>

    <div v-else class="preika-insight-empty">
      <span class="preika-insight-empty__icon"><CIcon name="cil-check-circle"/></span>
      <div>
        <strong>承認待ちはありません</strong>
        <p>現在、滞留している申請はありません。</p>
      </div>
    </div>
  </article>
</template>

<script>
export default {
  name: 'DashboardApprovalAging',

  props: {
    aging: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    value () {
      return this.aging || {}
    },

    totalCount () {
      return Number(this.value.totalCount || 0)
    },

    scopeLabel () {
      return this.value.scope === 'ACCOUNT' ? '全社' : '自分の承認'
    },

    hiddenItems () {
      return Number(this.value.hiddenItems || 0)
    },

    items () {
      return Array.isArray(this.value.items) ? this.value.items : []
    },

    segments () {
      const definitions = [
        { key: 'one', label: '0〜1日', count: Number(this.value.withinOneDayCount || 0), tone: 'fresh' },
        { key: 'three', label: '2〜3日', count: Number(this.value.twoToThreeDaysCount || 0), tone: 'watch' },
        { key: 'seven', label: '4〜7日', count: Number(this.value.fourToSevenDaysCount || 0), tone: 'warning' },
        { key: 'over', label: '8日以上', count: Number(this.value.eightDaysOrMoreCount || 0), tone: 'danger' }
      ]
      return definitions.map(segment => ({
        ...segment,
        width: this.totalCount ? (segment.count / this.totalCount) * 100 : 0
      }))
    }
  },

  methods: {
    agingTone (days) {
      const value = Number(days || 0)
      if (value >= 8) return 'preika-aging-item__days--danger'
      if (value >= 4) return 'preika-aging-item__days--warning'
      if (value >= 2) return 'preika-aging-item__days--watch'
      return 'preika-aging-item__days--fresh'
    }
  }
}
</script>
